import { calculateDistance, estimateDuration } from "./distanceCalculator";

export interface VehicleData {
  id: number;
  name: string;
  type: string;
  start_price: number;
  price: number; // per km rate
  time_price: number; // per minute rate
  min_price: number;
  passengers: number;
  luggage: number;
  image: string;
  features: string[];
}

export interface PricingResult {
  vehicleId: number;
  basePrice: number;
  distancePrice: number;
  timePrice: number;
  totalPrice: number;
  finalPrice: number; // after minimum price check
  distance: number;
  duration: string;
  durationMinutes: number;
}

/**
 * Calculate pricing for a specific vehicle based on route
 */
export const calculateVehiclePrice = (
  vehicle: VehicleData,
  pickupLat: number,
  pickupLon: number,
  destinationLat: number,
  destinationLon: number
): PricingResult => {
  // Use existing distance calculator
  const { km: distance } = calculateDistance(
    pickupLat,
    pickupLon,
    destinationLat,
    destinationLon
  );
  const duration = estimateDuration(distance);

  // Convert duration string to minutes for pricing calculation
  const durationMinutes = parseDurationToMinutes(duration);

  // Calculate pricing components
  const basePrice = vehicle.start_price;
  const distancePrice = vehicle.price * distance;
  const timePrice = vehicle.time_price * durationMinutes;
  const totalPrice = basePrice + distancePrice + timePrice;

  // Apply minimum price rule (from old site logic)
  const finalPrice = Math.max(totalPrice, vehicle.min_price);

  return {
    vehicleId: vehicle.id,
    basePrice,
    distancePrice,
    timePrice,
    totalPrice,
    finalPrice,
    distance,
    duration,
    durationMinutes,
  };
};

/**
 * Calculate pricing for all vehicles for a given route
 */
export const calculateAllVehiclePrices = (
  vehicles: VehicleData[],
  pickupLat: number,
  pickupLon: number,
  destinationLat: number,
  destinationLon: number
): PricingResult[] => {
  return vehicles.map((vehicle) =>
    calculateVehiclePrice(
      vehicle,
      pickupLat,
      pickupLon,
      destinationLat,
      destinationLon
    )
  );
};

/**
 * Convert duration string (like "1h 30min" or "45 min") to minutes
 */
const parseDurationToMinutes = (duration: string): number => {
  const hourMatch = duration.match(/(\d+)h/);
  const minuteMatch = duration.match(/(\d+)min/);

  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

  return hours * 60 + minutes;
};

/**
 * Format price for display
 */
export const formatPrice = (price: number): string => {
  return `€${price.toFixed(2)}`;
};

/**
 * Get price breakdown for display
 */
export const getPriceBreakdown = (
  pricing: PricingResult
): {
  base: string;
  distance: string;
  time: string;
  subtotal: string;
  final: string;
  isMinimumApplied: boolean;
} => {
  const isMinimumApplied = pricing.finalPrice > pricing.totalPrice;

  return {
    base: formatPrice(pricing.basePrice),
    distance: formatPrice(pricing.distancePrice),
    time: formatPrice(pricing.timePrice),
    subtotal: formatPrice(pricing.totalPrice),
    final: formatPrice(pricing.finalPrice),
    isMinimumApplied,
  };
};

/**
 * Default vehicle data based on current fleet
 * This matches the vehicles from VehicleFleet.tsx and old site
 */
export const DEFAULT_VEHICLES: VehicleData[] = [
  {
    id: 1,
    name: "Mercedes-Benz E 350 Sedan",
    type: "Saloon",
    start_price: 25,
    price: 2.1, // per km
    time_price: 0.5, // per minute
    min_price: 50,
    passengers: 4,
    luggage: 2,
    image: "/images/Sedan.webp",
    features: ["Premium Leather", "Climate Control", "WiFi"],
  },
  {
    id: 2,
    name: "Mercedes-Benz E 450 Wagon",
    type: "Station",
    start_price: 30,
    price: 2.2,
    time_price: 0.5,
    min_price: 55,
    passengers: 4,
    luggage: 3,
    image: "/images/Station.webp",
    features: ["Extra Space", "Premium Sound", "Navigation"],
  },
  {
    id: 3,
    name: "Mercedes-Benz V 250 D-Avantgarde",
    type: "Minivan",
    start_price: 40,
    price: 2.8,
    time_price: 0.7,
    min_price: 80,
    passengers: 8,
    luggage: 8,
    image: "/images/Minivan.webp",
    features: ["Group Travel", "Luxury Interior", "Entertainment"],
  },
  {
    id: 4,
    name: "Mercedes-Benz S 580 Sedan",
    type: "S-Class",
    start_price: 80,
    price: 3.5,
    time_price: 1.0,
    min_price: 150,
    passengers: 4,
    luggage: 3,
    image: "/images/S class.webp",
    features: ["Ultra Luxury", "Massage Seats", "Champagne Bar"],
  },
  {
    id: 5,
    name: "Mercedes-Benz Sprinter 519 CDI",
    type: "Minibus",
    start_price: 100,
    price: 4.0,
    time_price: 1.5,
    min_price: 225,
    passengers: 20,
    luggage: 0,
    image: "/images/sprint.webp",
    features: ["Large Groups", "Professional Driver", "Event Transport"],
  },
  {
    id: 6,
    name: "IVECO Daily Unvi C 70",
    type: "Minibus",
    start_price: 100,
    price: 4.0,
    time_price: 1.5,
    min_price: 225,
    passengers: 20,
    luggage: 0,
    image: "/images/ivaco.webp",
    features: ["Corporate Events", "Airport Transfers", "Tour Groups"],
  },
];

/**
 * Fetch vehicle data from API (matches old site)
 * Falls back to default data if API fails
 */
export const fetchVehicleData = async (): Promise<VehicleData[]> => {
  try {
    const response = await fetch("https://shop.aluminairaq.com/item/cars/all");
    if (!response.ok) throw new Error("API failed");

    const data = await response.json();

    // Map API response to our interface (adjust based on actual API structure)
    return data.cars || DEFAULT_VEHICLES;
  } catch (error) {
    console.warn("Failed to fetch vehicle data, using defaults:", error);
    return DEFAULT_VEHICLES;
  }
};
